import React, { useState } from 'react'
import styled from 'styled-components'
import { dimensions } from '~/constants/theme'

import Text, { FontStyle } from './Text'

interface StyledButtonProps {
  $cursor?: string
  $width?: string | number
  $height?: string | number
  $noPadding?: boolean
  $align?: string
  $borderRadius?: string | number
  $borderColor?: string
  $borderWidth?: string | number
  $color?: string
  $disabled?: boolean
}

const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  margin: 0;
  font: inherit;
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  padding: ${(props) => (props.$noPadding ? '0px' : '0 20px')};
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align};
  border-radius: ${(props) =>
    props.$borderRadius ? props.$borderRadius : dimensions.borderRadiusLG};
  border-color: ${(props) => props.$borderColor};
  border-width: ${(props) => props.$borderWidth}px;
  border-style: solid;
  box-sizing: border-box;
  background-color: ${(props) => props.$color};
  transition: all 0.2s ease-in-out;
  cursor: ${(props) =>
    props.$disabled ? 'default' : (props.$cursor ?? 'pointer')};

  > p {
    margin-right: ${(props) => (props.$noPadding ? '8px' : '0px')};
    margin-left: 8px;
  }
`

// The global `img { width: 100% }` rule overrides width/height attributes.
const toCssSize = (size: string | number) =>
  typeof size === 'number' || /^\d+(\.\d+)?$/.test(size) ? `${size}px` : size

interface ImageButtonProps {
  children?: React.ReactNode
  'aria-label'?: string
  icon?: SVGAsset
  activeIcon?: SVGAsset
  disabledIcon?: SVGAsset
  hoverIcon?: SVGAsset
  disabled?: boolean
  title?: string
  cursor?: string
  align?: string
  width?: string | number
  height?: string | number
  imgWidth?: string | number
  imgHeight?: string | number
  borderColor?: string
  borderWidth?: string | number
  borderRadius?: string | number
  textColor?: string
  textHoverColor?: string
  textActiveColor?: string
  textDisabledColor?: string
  backColor?: string
  hoveredColor?: string
  activeColor?: string
  disabledColor?: string
  style?: React.CSSProperties
  noPadding?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

const ImageButton = ({
  children,
  disabled = false,
  title,
  icon,
  cursor = 'pointer',
  hoverIcon,
  activeIcon,
  disabledIcon,
  align = 'center',
  imgWidth = '16',
  width = '96px',
  imgHeight = '17',
  height = '36px',
  backColor = 'var(--chakra-colors-brand-solid)',
  hoveredColor = 'var(--chakra-colors-brand-emphasized)',
  activeColor = 'var(--chakra-colors-brand-solid)',
  disabledColor = 'var(--chakra-colors-border-subtle)',
  borderColor = 'var(--chakra-colors-transparent)',
  borderWidth = 0,
  textColor = 'var(--chakra-colors-brand-contrast)',
  textHoverColor = 'var(--chakra-colors-brand-contrast)',
  textActiveColor = 'var(--chakra-colors-bg-subtle)',
  textDisabledColor = 'var(--chakra-colors-brand-contrast)',
  onClick,
  noPadding,
  ...rest
}: ImageButtonProps) => {
  const [hovered, setHovered] = useState(false)
  const [clicked, setClicked] = useState(false)

  const handleEntered = () => {
    setHovered(true)
  }

  const handleLeave = () => {
    setHovered(false)
  }

  const handleMouseDown = () => {
    setClicked(true)
  }

  const handleMouseUp = () => {
    setClicked(false)
  }

  let color = backColor
  let tColor = textColor
  let resolvedIcon = icon
  if (hovered) {
    color = hoveredColor
    tColor = textHoverColor
    resolvedIcon = hoverIcon ?? icon
  }
  if (clicked) {
    color = activeColor
    tColor = textActiveColor
    resolvedIcon = activeIcon ?? icon
  }
  if (disabled) {
    color = disabledColor
    tColor = textDisabledColor
    resolvedIcon = disabledIcon ?? icon
  }

  let resolvedNoPadding = noPadding !== undefined ? noPadding : false
  if (!resolvedNoPadding && (!icon || !title)) {
    resolvedNoPadding = true
  }

  return (
    <StyledButton
      type="button"
      disabled={disabled}
      $cursor={cursor}
      $width={width}
      $height={height}
      $color={color}
      $align={align}
      $disabled={disabled}
      $noPadding={resolvedNoPadding}
      $borderColor={borderColor}
      $borderWidth={borderWidth}
      onClick={(e) => !disabled && onClick && onClick(e)}
      onMouseEnter={handleEntered}
      onMouseLeave={handleLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...rest}
    >
      {children ?? (
        <>
          {resolvedIcon && (
            <img
              src={resolvedIcon}
              alt=""
              width={imgWidth}
              height={imgHeight}
              style={{
                objectFit: 'contain',
                width: toCssSize(imgWidth),
                height: toCssSize(imgHeight),
              }}
            />
          )}
          {title && (
            <Text
              fontStyle={FontStyle.Small}
              color={tColor}
              text={title}
              weight="600"
            />
          )}
        </>
      )}
    </StyledButton>
  )
}

export default ImageButton
